/**
 * Admin Statistics
 * Detailed platform analytics
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, Users, Briefcase, BarChart3, Loader2
} from 'lucide-react';
import axios from 'axios';
import { auth } from '../../utils/auth';

const API = process.env.REACT_APP_BACKEND_URL;

interface Stats {
  user_growth: Array<{ date: string; total_users: number }>;
  cases_by_status: Record<string, number>;
  top_tenants: Array<{ _id: string; name: string; case_count: number }>;
}

const AdminStats: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchStats();
  }, []);
  
  const fetchStats = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  // Calculate growth
  const userGrowth = stats?.user_growth || [];
  const currentUsers = userGrowth[userGrowth.length - 1]?.total_users || 0;
  const previousUsers = userGrowth[0]?.total_users || 0;
  const growthPercent = previousUsers > 0 
    ? Math.round(((currentUsers - previousUsers) / previousUsers) * 100)
    : 0;
  
  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Statistikk</h1>
        <p className="text-slate-400 mt-1">Detaljert plattformanalyse</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User growth chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-white">Brukervekst</h3>
              <p className="text-sm text-slate-400">Siste 30 dager</p>
            </div>
            <div className={`flex items-center gap-1 text-sm font-medium ${
              growthPercent >= 0 ? 'text-emerald-400' : 'text-red-400'
            }`}>
              <TrendingUp className="w-4 h-4" />
              {growthPercent >= 0 ? '+' : ''}{growthPercent}%
            </div>
          </div>
          
          {/* Simple bar chart */}
          <div className="h-48 flex items-end gap-1">
            {userGrowth.slice(-30).map((day, i) => {
              const maxUsers = Math.max(...userGrowth.map(d => d.total_users));
              const height = maxUsers > 0 ? (day.total_users / maxUsers) * 100 : 0;
              return (
                <div
                  key={i}
                  className="flex-1 bg-violet-600/50 hover:bg-violet-500/70 rounded-t transition-colors"
                  style={{ height: `${Math.max(height, 2)}%` }}
                  title={`${day.date}: ${day.total_users} brukere`}
                />
              );
            })}
          </div>
          
          <div className="flex justify-between mt-4 text-xs text-slate-500">
            <span>30 dager siden</span>
            <span>I dag</span>
          </div>
        </motion.div>
        
        {/* Cases by status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <Briefcase className="w-5 h-5 text-slate-400" />
            <h3 className="font-semibold text-white">Saker etter status</h3>
          </div>
          
          <div className="space-y-4">
            {Object.entries(stats?.cases_by_status || {}).map(([status, count]) => {
              const total = Object.values(stats?.cases_by_status || {}).reduce((a, b) => a + b, 0);
              const percent = total > 0 ? (count / total) * 100 : 0;
              const colors: Record<string, string> = {
                OPEN: 'bg-blue-500',
                IN_PROGRESS: 'bg-amber-500',
                COMPLETED: 'bg-emerald-500',
                ARCHIVED: 'bg-slate-500'
              };
              const labels: Record<string, string> = {
                OPEN: 'Åpne',
                IN_PROGRESS: 'Under arbeid',
                COMPLETED: 'Fullført',
                ARCHIVED: 'Arkivert'
              };
              
              return (
                <div key={status}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">{labels[status] || status}</span>
                    <span className="text-slate-500">{count}</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${colors[status] || 'bg-slate-600'} rounded-full`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
        
        {/* Top tenants */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl p-6 lg:col-span-2"
        >
          <div className="flex items-center gap-3 mb-6">
            <BarChart3 className="w-5 h-5 text-slate-400" />
            <h3 className="font-semibold text-white">Mest aktive firmaer</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {(stats?.top_tenants || []).slice(0, 10).map((tenant, i) => (
              <div
                key={tenant._id}
                className="bg-slate-800/50 rounded-xl p-4 text-center"
              >
                <div className="w-8 h-8 bg-violet-600/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-sm font-bold text-violet-400">#{i + 1}</span>
                </div>
                <p className="text-sm font-medium text-white truncate">{tenant.name}</p>
                <p className="text-xs text-slate-500 mt-1">{tenant.case_count} saker</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminStats;
